import * as React from 'react';
import Cropper from 'react-easy-crop';
import Modal from 'react-modal';
import {ALVO, AppAssetKind} from './appAssetCrop';
import {
  AppAssetPreview,
  BotaoAsset,
  PreviewProps,
  TextoAuxiliar,
} from './AppAssetPresentation';
import {useAppAssetCropModal} from './AppAssetCropModal.use-case';
import {Superficie, Colunas, Painel, Rodape} from './AppAssetCropModal.style';
import {AppAssetGuideOverlay} from './AppAssetGuideOverlay';
import {AppAssetCropControls} from './AppAssetCropControls';
import {AppAssetColorPicker} from './AppAssetColorPicker';

interface Props extends Omit<PreviewProps, 'url'> {
  arquivo: File;
  kind: AppAssetKind;
  corInicial?: string;
  onCancel: () => void;
  onConfirm: (arquivo: File) => void;
}

export const AppAssetCropModal = (props: Props) => {
  const estado = useAppAssetCropModal(props);
  const [guias, setGuias] = React.useState(true);
  const [caixa, setCaixa] = React.useState({width: 0, height: 0});
  const titulo =
    props.kind === 'icon'
      ? 'Ajuste o ícone do seu app'
      : 'Prepare a tela de abertura';
  const dimensao = ALVO[props.kind].largura;
  const appElements = React.useMemo(
    () =>
      typeof document === 'undefined'
        ? undefined
        : Array.from(
            document.querySelectorAll<HTMLElement>(
              '[data-react-class="routes/admin/app/AppAssetFields"]',
            ),
          ),
    [],
  );
  return (
    <Modal
      isOpen
      onRequestClose={estado.cancelar}
      contentLabel={titulo}
      appElement={appElements}
      shouldCloseOnOverlayClick={false}
      shouldCloseOnEsc
      style={{
        overlay: {
          zIndex: 10000,
          backgroundColor: 'rgba(19, 35, 54, .72)',
          overflowY: 'auto',
          padding: '16px 10px',
        },
        content: {
          position: 'relative',
          inset: 'auto',
          padding: 0,
          border: 'none',
          borderRadius: 18,
          width: '100%',
          maxWidth: 980,
          margin: '0 auto',
          overflow: 'hidden',
          boxShadow: '0 24px 90px #10203555',
          background: '#fff',
        },
      }}
    >
      <Superficie>
        <div
          style={{
            padding: '22px 24px',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 16,
            justifyContent: 'space-between',
            borderBottom: '1px solid #e2e9f0',
          }}
        >
          <div>
            <h2>{titulo}</h2>
            <TextoAuxiliar>
              Uma imagem bem enquadrada, em todas as telas.
            </TextoAuxiliar>
          </div>
          <BotaoAsset
            type="button"
            aria-label="Fechar e descartar este recorte"
            onClick={estado.cancelar}
            style={{padding: '8px 13px', fontSize: 20}}
          >
            ×
          </BotaoAsset>
        </div>
        <Colunas>
          <Painel>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: 8,
                marginBottom: 12,
              }}
            >
              <h3 style={{margin: 0}}>Enquadramento</h3>
              <BotaoAsset
                type="button"
                disabled={estado.processando}
                aria-pressed={guias}
                onClick={() => setGuias(!guias)}
                style={{padding: '6px 10px', fontSize: 12}}
              >
                Guias {guias ? 'visíveis' : 'ocultas'}
              </BotaoAsset>
            </div>
            <div
              style={{
                position: 'relative',
                aspectRatio: '1',
                background: '#23364b',
                borderRadius: 12,
                overflow: 'hidden',
              }}
            >
              {estado.urlOrigem && (
                <Cropper
                  image={estado.urlOrigem}
                  crop={estado.posicao}
                  zoom={estado.zoom}
                  aspect={1}
                  showGrid={false}
                  restrictPosition
                  zoomWithScroll={!estado.processando}
                  cropperProps={{
                    'aria-disabled': estado.processando,
                    tabIndex: estado.processando ? -1 : 0,
                  }}
                  onCropChange={estado.setPosicao}
                  onZoomChange={estado.setZoom}
                  onCropAreaChange={(_visivel, pixels) =>
                    estado.setArea(pixels)
                  }
                  onCropSizeChange={setCaixa}
                  style={{
                    cropAreaStyle: {border: '1px solid #ffffffaa'},
                    containerStyle: {
                      pointerEvents: estado.processando ? 'none' : 'auto',
                    },
                    mediaStyle: {transition: 'none'},
                  }}
                />
              )}
              {guias && caixa.width > 0 && (
                <AppAssetGuideOverlay
                  kind={props.kind}
                  caixa={caixa}
                  margem={estado.margem}
                />
              )}
            </div>
            <TextoAuxiliar>
              Arraste para enquadrar. A guia verde indica onde manter o conteúdo
              principal no arquivo final.
            </TextoAuxiliar>
            <AppAssetCropControls
              kind={props.kind}
              zoom={estado.zoom}
              setZoom={estado.setZoom}
              margem={estado.margem}
              setMargem={estado.setMargem}
              processando={estado.processando}
            />
            <AppAssetColorPicker
              cor={estado.cor}
              setCor={estado.setCor}
              processando={estado.processando}
            />
          </Painel>
          <Painel
            style={{background: '#eff4f8', borderLeft: '1px solid #e2e9f0'}}
          >
            <h3>Veja no aplicativo</h3>
            <TextoAuxiliar>
              {props.kind === 'icon'
                ? 'Seu ícone na tela inicial, com a margem e a cor escolhidas.'
                : 'O centro aparece em todos os formatos. As bordas podem ser cortadas.'}
            </TextoAuxiliar>
            <div style={{marginTop: 24}}>
              <AppAssetPreview {...props} url={estado.preview} />
            </div>
            <div
              style={{
                marginTop: 24,
                padding: '12px 14px',
                background: '#ffffffb0',
                borderRadius: 10,
              }}
            >
              <TextoAuxiliar>
                {props.kind === 'icon'
                  ? 'As máscaras iOS e Android são guias. O arquivo salvo será quadrado, com fundo opaco.'
                  : 'Guias ilustrativas para celular 9:19,5, tablet 3:4 e paisagem 16:9. A exibição pode variar conforme o aparelho.'}
              </TextoAuxiliar>
            </div>
            {estado.upscale && (
              <p
                role="status"
                style={{
                  background: '#fff3d8',
                  color: '#76500a',
                  padding: 12,
                  borderRadius: 9,
                  fontSize: 13,
                }}
              >
                Esta imagem será ampliada mais de 1,5× e pode perder nitidez. Se
                possível, use uma versão maior.
              </p>
            )}
            {estado.erro && (
              <p
                role="alert"
                style={{
                  background: '#fce9e8',
                  color: '#a12620',
                  padding: 12,
                  borderRadius: 9,
                }}
              >
                {estado.erro}
              </p>
            )}
          </Painel>
        </Colunas>
        <Rodape>
          <span style={{fontSize: 13, color: '#586b80'}}>
            PNG · {dimensao} × {dimensao} px
          </span>
          <div style={{display: 'flex', gap: 8}}>
            <BotaoAsset type="button" onClick={estado.cancelar}>
              Cancelar
            </BotaoAsset>
            <BotaoAsset
              type="button"
              data-primary="true"
              disabled={!estado.pronto || estado.processando}
              onClick={estado.confirmar}
            >
              {estado.processando ? 'Preparando…' : 'Usar imagem'}
            </BotaoAsset>
          </div>
        </Rodape>
      </Superficie>
    </Modal>
  );
};
